/**
 * Voyado Elevate: recommendation carousels.
 *
 * Custom element `<elevate-recommendations>`, one per section instance, but
 * the fetch is shared: Voyado's own reference implementation makes one
 * `landingPage` call per Elevate page, no matter how many recommendation
 * lists a merchandiser drops onto it, so a page carrying three of these
 * sections still queries Elevate once. Elements register themselves
 * synchronously as they connect, then a microtask groups everything that
 * registered in this tick by page reference before firing. Product pages
 * batch the same way, one `productPage` call per product - see the
 * data-product-key branch below.
 *
 * On a landing page, the list's own algorithm, ranking and product count
 * are whatever the merchandiser configured in Experience for that id -
 * this file only names which list to read out of the response, same
 * principle as elevate-autocomplete.js's startPageRecommendations(). There
 * is no equivalent per-list Experience config for a product page, so PDP
 * instances name their own algorithm directly (data-algorithm) instead -
 * confirmed via a live query.productPage call, no pageReference needed,
 * just {productKey}. See sections/elevate-pdp-recommendations.liquid.
 *
 * Rendering uses native product-card.liquid, via
 * assets/elevate-native-cards.js - required for SAMI Product Labels
 * compatibility and native member pricing, confirmed empirically neither
 * works against Elevate's own .elevate-card markup. The rendered
 * `.collection-product-card` is either a grid item (layout: grid) or, with
 * `swiper-slide` added, a Swiper slide (layout: slider).
 *
 * An instance placed inside sections/elevate-search.liquid's no-hits state
 * does not register until that state is actually revealed, so a normal
 * search with hits never fires this file's request at all. See
 * #registerWhenVisible. PDP instances never defer this way - a product
 * page unconditionally has a product to fetch recommendations for.
 *
 * Requires elevate-core.js and elevate-native-cards.js. Swiper is loaded
 * globally from layout/theme.liquid.
 */

(function () {
  'use strict';

  const E = window.CGElevate;
  const NativeCards = window.CGElevateNativeCards;

  // Wishlist hearts: NativeCards.render() asks the Wishlist Guru app to draw
  // them once the cards are in, see requestWishlistHearts() in
  // elevate-native-cards.js. The carousel wrapper still needs the theme's
  // `product-carousel__wrapper` class for the app to recognise it (added in
  // snippets/elevate-recommendations-block.liquid and
  // snippets/elevate-cart-recs-carousels.liquid).

  const batches = new Map();
  let flushScheduled = false;

  // batchKey is 'landingPage:<pageReference>' or 'productPage:<productKey>' -
  // productKey is always numeric and pageReference always starts with '/'
  // (see E.normalizePageReference), so the two modes could not collide even
  // unprefixed, but the prefix keeps runBatch's dispatch explicit.
  function register(batchKey, listId, element, algorithm) {
    let batch = batches.get(batchKey);
    if (!batch) {
      batch = new Map();
      batches.set(batchKey, batch);
    }
    if (!batch.has(listId)) batch.set(listId, { elements: [], algorithm: algorithm });
    batch.get(listId).elements.push(element);

    if (!flushScheduled) {
      flushScheduled = true;
      queueMicrotask(flush);
    }
  }

  function flush() {
    flushScheduled = false;
    const entries = Array.from(batches.entries());
    batches.clear();

    E.whenReady().then((elevate) => {
      entries.forEach(([batchKey, batch]) => runBatch(elevate, batchKey, batch));
    });
  }

  async function runBatch(elevate, batchKey, batch) {
    if (!elevate) {
      batch.forEach(({ elements }) => elements.forEach((element) => element.renderEmpty()));
      return;
    }

    const separator = batchKey.indexOf(':');
    const mode = batchKey.slice(0, separator);
    const key = batchKey.slice(separator + 1);
    const isProductPage = mode === 'productPage';

    // A product page always names its algorithm; a landing page usually does
    // not, since an Experience-configured list already carries one. The
    // exception is a visitor-scoped algorithm like RECENTLY_VIEWED, which
    // Voyado documents as a landingPage query but which nothing in Experience
    // needs to configure - so the theme names it and there is no list to look
    // up. Hence keying off the algorithm being present rather than off mode.
    const recommendationLists = Array.from(batch.entries()).map(([id, entry]) =>
      entry.algorithm ? { id: id, algorithm: entry.algorithm } : { id: id }
    );

    let data;
    try {
      data = isProductPage
        ? await elevate.query.productPage({ productKey: key }, { recommendationLists: recommendationLists })
        : await elevate.query.landingPage({ pageReference: key }, { recommendationLists: recommendationLists });
    } catch (e) {
      data = null;
    }

    const lists = (data && data.recommendationLists) || [];
    batch.forEach(({ elements }, listId) => {
      const list = lists.filter(Boolean).find((candidate) => candidate.id === listId);
      const groups = (list && list.productGroups) || [];
      elements.forEach((element) => element.render(groups));
    });
  }

  class ElevateRecommendations extends HTMLElement {
    #swiper = null;

    connectedCallback() {
      this.refs = { track: this.querySelector('[data-elevate-rec-track]') };

      const listId = this.dataset.listId || '';
      if (!listId) return;

      const productKey = this.dataset.productKey || '';
      if (productKey) {
        const algorithm = this.dataset.algorithm || '';
        if (!algorithm) return;
        register('productPage:' + productKey, listId, this, algorithm);
        return;
      }

      // Landing-page mode may still name an algorithm - see runBatch. Empty
      // for an ordinary Experience-configured list, which is the usual case.
      const landingAlgorithm = this.dataset.algorithm || '';

      const pageReference = E.normalizePageReference(this.dataset.pageReference);
      // Neither can be worked out for this page, or the merchandiser has not
      // filled in the list id yet. Leave the section hidden rather than fire
      // a request Elevate can only reject.
      if (!pageReference) return;

      // Placed inside sections/elevate-search.liquid's no-hits state: that
      // block starts hidden and elevate-list.js only reveals it once a
      // search genuinely returns 0 hits. Registering immediately would fire
      // a landingPage call on every search, hit or not.
      const emptyState = this.closest('[data-elevate-empty]');
      if (emptyState) {
        this.#registerWhenVisible(emptyState, pageReference, listId, landingAlgorithm);
        return;
      }

      register('landingPage:' + pageReference, listId, this, landingAlgorithm);
    }

    #registerWhenVisible(emptyState, pageReference, listId, algorithm) {
      if (!emptyState.hidden) {
        register('landingPage:' + pageReference, listId, this, algorithm);
        return;
      }

      const observer = new MutationObserver(() => {
        if (emptyState.hidden) return;
        observer.disconnect();
        register('landingPage:' + pageReference, listId, this, algorithm);
      });
      observer.observe(emptyState, { attributes: true, attributeFilter: ['hidden'] });
    }

    disconnectedCallback() {
      if (this.#swiper) {
        this.#swiper.destroy();
        this.#swiper = null;
      }
    }

    renderEmpty() {
      // No SDK: uninstalled app, or consent management blocking the embed.
      // Same normal-state handling as elevate-list.js.
    }

    async render(groups) {
      if (!groups.length) return;

      this.refs.track.innerHTML = '';
      await NativeCards.render(groups, this.refs.track);

      if (this.dataset.layout === 'slider') this.#initSlider();
      this.hidden = false;
    }

    #initSlider() {
      const cards = this.refs.track.querySelectorAll('.collection-product-card');
      cards.forEach((card) => card.classList.add('swiper-slide'));

      // Merchandiser setting (sections/elevate-recommendations.liquid /
      // sections/elevate-search.liquid's "recommendations" block) - how many
      // cards show at once on desktop. Smaller breakpoints stay fixed rather
      // than scaling with it, so a merchandiser raising the desktop count
      // cannot accidentally cram the mobile/tablet layout too.
      const desktopCardsVisible = parseInt(this.dataset.cardsVisible, 10) || 4;
      const tabletCardsVisible = Math.min(desktopCardsVisible, 3);

      // Phones, under 576px. Its own setting rather than a share of the
      // desktop count, for the reason above: the two are different layout
      // decisions. Clamped to 1 or 2 because that is what the theme's
      // `products--N-items-mobile` typography exists for - a 3 here would
      // render three cards at two-up sizing.
      const mobileCardsVisible = parseInt(this.dataset.columnsMobile, 10) === 1 ? 1 : 2;

      // slidesPerView is NOT clamped to the number of cards. Clamping it
      // makes a one-product list render that product across the full width
      // of the section instead of at card size with space beside it - which
      // is what a "recently viewed" list looks like on a visitor's first
      // product page.
      //
      // Looping is what actually needs the clamp: Swiper cannot loop through
      // fewer slides than it shows at once, and left on it renders blanks.
      // So the card count decides whether to loop, not how wide a card is.
      const canLoop = cards.length > desktopCardsVisible;

      this.#swiper = new Swiper(this.querySelector('.elevate-recommendations__swiper'), {
        loop: canLoop,
        autoHeight: true,
        allowTouchMove: true,
        slidesPerView: desktopCardsVisible,
        breakpoints: {
          0: { slidesPerView: mobileCardsVisible },
          576: { slidesPerView: 2 },
          750: { slidesPerView: tabletCardsVisible },
          1200: { slidesPerView: desktopCardsVisible }
        },
        lazy: { loadPrevNext: true, loadPrevNextAmount: 2 },
        preloadImages: true,
        spaceBetween: 24,
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev')
        }
      });
    }
  }

  window.customElements.define('elevate-recommendations', ElevateRecommendations);
})();
